// Promociones horarias (price rules) · motor de PREVISUALIZACIÓN del TPV.
//
// Port JS puro del motor del API (el SERVIDOR decide el precio al comandar;
// esto solo pinta en el TPV lo que el servidor va a aplicar). Misma
// semántica:
//   • Hora LOCAL del restaurante. En el TPV la hora sale de `nowLocal` del
//     API (`GET price-rules/active`) + el tiempo transcurrido desde que se
//     recibió → inmune al reloj mal puesto del dispositivo. Los tests y los
//     fixtures pueden pasar un instante UTC + zona horaria IANA (DST real).
//   • Día de la semana del momento local (bitmask L=1, M=2, X=4, J=8,
//     V=16, S=32, D=64). Una franja que cruza medianoche (23:00–02:00)
//     pertenece al día en que EMPIEZA: el sábado a la 01:00 cuenta como
//     viernes (y la vigencia por fechas se mira con esa fecha de inicio).
//   • Franja [inicio, fin): fin exclusivo. Sin horas = todo el día; solo
//     inicio = hasta las 24:00; solo fin = desde las 00:00; inicio == fin
//     (con hora) = 24 h desde el inicio.
//   • Una sola regla por línea: gana la de mayor `priority`; empate → la
//     que deja el precio más bajo; empate total → menor id (estable).
//   • Solo el precio BASE del producto (los complementos no se tocan).
//   • Una regla que no rebaja (p. ej. FIXED_PRICE por encima del precio
//     base) no es candidata: cae a la siguiente.
//   • PERCENT_OFF: precio = base − redondeo(base × %), como el API.
//   • Categoría: casa con la del producto, su subcategoría y la categoría
//     padre (un nivel), como el API.
//   • Productos al peso: solo PERCENT_OFF.
//   • Líneas «Otros»/precio libre (priceByMarket) y líneas con precio
//     editado a mano no se tocan.
//   • Redondeo a céntimos (half away from zero, como Math.Round(x, 2,
//     MidpointRounding.AwayFromZero) en .NET).

export const PRICE_RULE_KINDS = Object.freeze({
  PERCENT_OFF: "PERCENT_OFF",
  AMOUNT_OFF: "AMOUNT_OFF",
  FIXED_PRICE: "FIXED_PRICE",
});

export const PRICE_RULE_SCOPES = Object.freeze({
  ALL: "ALL",
  CATEGORY: "CATEGORY",
  PRODUCT: "PRODUCT",
});

export const PRICE_RULE_CHANNELS = Object.freeze({ TPV: 1, QR: 2 });

// Bitmask por día ISO (1 = lunes … 7 = domingo).
export const WEEKDAY_BITS = Object.freeze([1, 2, 4, 8, 16, 32, 64]);
export const ALL_WEEKDAYS = 127;
export const WEEKDAY_SHORT = Object.freeze(["L", "M", "X", "J", "V", "S", "D"]);

const pad2 = (n) => String(n).padStart(2, "0");

/** Redondeo a céntimos, mitad lejos de cero, robusto a 1.005 → 1.01. */
export function roundCents(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  const scaled = Number((Math.abs(n) * 100).toPrecision(12));
  return (sign * Math.round(scaled)) / 100;
}

/** "HH:mm" → minutos del día; null/"" → null; inválido → null. */
export function parseHHmm(s) {
  if (s == null || s === "") return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(s).trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 24 || mi > 59) return null;
  return h * 60 + mi;
}

// ── Momento local ────────────────────────────────────────────────────────

// Fecha civil (y, m, d) → día ISO de la semana (1 = lunes).
function isoWeekdayOf(y, m, d) {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = domingo
  return dow === 0 ? 7 : dow;
}

function shiftDate(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

function momentFromParts(y, mo, d, h, mi) {
  return {
    date: `${y}-${pad2(mo)}-${pad2(d)}`,
    minutes: h * 60 + mi,
    weekday: isoWeekdayOf(y, mo, d),
  };
}

/**
 * Momento local a partir de un string de pared "yyyy-MM-ddTHH:mm[:ss]"
 * (sin zona: ya es hora local del restaurante).
 */
export function momentFromLocalString(local) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(local || ""));
  if (!m) return null;
  return momentFromParts(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]));
}

/** Momento local de un instante UTC en una zona IANA (DST incluido). */
export function momentFromInstant(instant, timeZone) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  if (!timeZone) {
    return momentFromParts(
      date.getFullYear(), date.getMonth() + 1, date.getDate(),
      date.getHours(), date.getMinutes(),
    );
  }
  const parts = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .formatToParts(date)
    .forEach((p) => { parts[p.type] = p.value; });
  return momentFromParts(
    Number(parts.year), Number(parts.month), Number(parts.day),
    Number(parts.hour) % 24, Number(parts.minute),
  );
}

/**
 * Hora local del restaurante "ahora" sin depender del reloj del
 * dispositivo para la hora absoluta: `nowLocal` (pared, del API) más lo
 * transcurrido desde que llegó la respuesta (`fetchedAtMs`). El reloj del
 * dispositivo solo se usa para medir el delta (unos segundos/minutos).
 */
export function localMomentFromServer({ nowLocal, fetchedAtMs }, nowMs = Date.now()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(nowLocal || ""));
  if (!m) return null;
  const baseMs = Date.UTC(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4]), Number(m[5]), Number(m[6] || 0),
  );
  const elapsed = Number.isFinite(fetchedAtMs) ? Math.max(0, nowMs - fetchedAtMs) : 0;
  const t = new Date(baseMs + elapsed);
  return momentFromParts(
    t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate(),
    t.getUTCHours(), t.getUTCMinutes(),
  );
}

/** Acepta {utc, timeZone} | {local} | string local | momento ya hecho. */
export function toMoment(at) {
  if (!at) return null;
  if (typeof at === "string") return momentFromLocalString(at);
  if (at.date && Number.isFinite(at.minutes) && at.weekday) return at;
  if (at.local) return momentFromLocalString(at.local);
  if (at.utc) return momentFromInstant(at.utc, at.timeZone);
  return null;
}

// ── Coincidencia de una regla ────────────────────────────────────────────

const dayBit = (isoWeekday) => WEEKDAY_BITS[isoWeekday - 1];

function weekdaysMask(rule) {
  // Sin campo → todos (default del API); 0 → ningún día (igual que el API).
  if (rule?.weekdays == null) return ALL_WEEKDAYS;
  const w = Number(rule.weekdays);
  return Number.isFinite(w) ? w : ALL_WEEKDAYS;
}

function dateInRange(rule, dateStr) {
  const from = rule?.validFrom ? String(rule.validFrom).slice(0, 10) : null;
  const to = rule?.validTo ? String(rule.validTo).slice(0, 10) : null;
  if (from && dateStr < from) return false;
  if (to && dateStr > to) return false;
  return true;
}

/**
 * ¿La franja/días/fechas de la regla cubren el momento? Devuelve la fecha
 * del día de INICIO de la franja que lo cubre (o null).
 */
export function ruleWindowStartDate(rule, moment) {
  if (!rule || !moment) return null;
  const mask = weekdaysMask(rule);
  const start = parseHHmm(rule.startTime);
  const end = parseHHmm(rule.endTime);
  const s = start == null ? 0 : start;
  const e = end == null ? 24 * 60 : end;
  const now = moment.minutes;

  const sameDay = (d, wd) => ((mask & dayBit(wd)) !== 0 && dateInRange(rule, d) ? d : null);

  if (start == null && end == null) {
    // Todo el día.
    return sameDay(moment.date, moment.weekday);
  }
  if (s < e) {
    if (now >= s && now < e) return sameDay(moment.date, moment.weekday);
    return null;
  }
  // Cruza medianoche (o inicio == fin → 24 h desde el inicio):
  // [s, 24:00) de hoy o [00:00, e) de la franja que empezó ayer.
  if (now >= s) {
    const today = sameDay(moment.date, moment.weekday);
    if (today) return today;
  }
  if (now < e) {
    const prevWd = moment.weekday === 1 ? 7 : moment.weekday - 1;
    return sameDay(shiftDate(moment.date, -1), prevWd);
  }
  return null;
}

export function ruleMatchesMoment(rule, moment) {
  return ruleWindowStartDate(rule, moment) != null;
}

/**
 * Categorías con las que casa un producto: la suya, su subcategoría y la
 * categoría padre (un nivel). `parentOf(id)` → id del padre o null.
 */
export function productCategoryIds(product, parentOf = null) {
  const ids = [];
  const add = (v) => {
    if (v == null || v === "") return;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0 || ids.includes(n)) return;
    ids.push(n);
  };
  const own = product?.idCategory ?? product?.categoryId ?? product?.idCategoria ?? null;
  add(own);
  add(product?.subCategory ?? product?.idSubCategory ?? null);
  if (own != null && typeof parentOf === "function") add(parentOf(Number(own)));
  return ids;
}

const ruleIds = (rule, flatKey, targetKey) =>
  [
    ...(Array.isArray(rule?.[flatKey]) ? rule[flatKey] : []),
    ...(Array.isArray(rule?.targets) ? rule.targets.map((t) => t?.[targetKey]) : []),
  ]
    .filter((v) => v != null)
    .map(Number);

export function ruleTargetsProduct(rule, product, categoryIds = null) {
  const scope = String(rule?.scope || PRICE_RULE_SCOPES.ALL).toUpperCase();
  if (scope === PRICE_RULE_SCOPES.ALL) return true;
  if (scope === PRICE_RULE_SCOPES.PRODUCT) {
    return ruleIds(rule, "productIds", "productId").includes(Number(product?.id));
  }
  if (scope === PRICE_RULE_SCOPES.CATEGORY) {
    const cats = Array.isArray(categoryIds) ? categoryIds.map(Number) : productCategoryIds(product);
    if (cats.length === 0) return false;
    const targets = ruleIds(rule, "categoryIds", "categoryId");
    return cats.some((c) => targets.includes(c));
  }
  return false;
}

function zoneMatches(rule, zonaId) {
  const zones = Array.isArray(rule?.zones)
    ? rule.zones
    : Array.isArray(rule?.zoneIds)
      ? rule.zoneIds
      : [];
  if (zones.length === 0) return true;
  if (zonaId == null) return false;
  return zones.some((z) => Number(z) === Number(zonaId));
}

function channelMatches(rule, channel) {
  // Sin campo → TPV (default del API); 0 → ningún canal (igual que el API).
  const c = rule?.channels == null ? PRICE_RULE_CHANNELS.TPV : Number(rule.channels);
  return ((Number.isFinite(c) ? c : PRICE_RULE_CHANNELS.TPV) & channel) !== 0;
}

// ── Precio ───────────────────────────────────────────────────────────────

/**
 * Precio resultante de aplicar UNA regla al precio base, o null si no
 * aplica (valor inválido, fijo por encima del base, peso no-porcentaje…).
 */
export function computeRulePrice(rule, basePrice, { isWeight = false } = {}) {
  const base = Number(basePrice);
  const v = Number(rule?.value);
  if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(v)) return null;
  const kind = String(rule?.kind || "").toUpperCase();
  if (isWeight && kind !== PRICE_RULE_KINDS.PERCENT_OFF) return null;
  let out;
  switch (kind) {
    case PRICE_RULE_KINDS.PERCENT_OFF:
      // Igual que el API: se redondea el DESCUENTO y se resta.
      out = base - roundCents((base * Math.min(100, Math.max(0, v))) / 100);
      break;
    case PRICE_RULE_KINDS.AMOUNT_OFF:
      out = base - Math.max(0, v);
      break;
    case PRICE_RULE_KINDS.FIXED_PRICE:
      out = Math.min(Math.max(0, v), base);
      break;
    default:
      return null;
  }
  out = roundCents(Math.max(0, out));
  // Una regla que no rebaja no es candidata.
  return out < base ? out : null;
}

const isWeightProduct = (p) => !!(p?.ventaAlPeso);

/** Líneas que el motor nunca toca (precio libre «Otros» o editado). */
export function isLineExcluded(product, line) {
  const l = line || {};
  if (product?.priceByMarket || l.priceByMarket) return true;
  if (l.isCustom || product?.isCustom) return true;
  if (l.precioOriginal != null && Number(l.precioOriginal) !== Number(l.precio)) return true;
  if (l.edited || l.isEdited) return true;
  return false;
}

/**
 * Elige la regla ganadora para un producto en un momento.
 *
 * @param {object} args
 * @param {object} args.product  producto del catálogo ({id, precio, idCategory, ventaAlPeso, priceByMarket})
 * @param {number} [args.basePrice] precio base (por defecto product.precio; para peso, el importe ya pesado)
 * @param {object} [args.line]   línea del carrito (para excluir editadas/Otros)
 * @param {Array}  args.rules    reglas activas
 * @param {object|string} args.at momento (ver `toMoment`)
 * @param {number|null} [args.zonaId]
 * @param {number[]} [args.categoryIds] categorías del producto (propia, sub y padre); por defecto se derivan del producto
 * @param {number} [args.channel] 1 = TPV (default)
 * @returns {null | {precio:number, precioCarta:number, priceRuleId:any, priceRuleName:string}}
 */
export function evaluatePrice({
  product,
  basePrice,
  line = null,
  rules,
  at,
  zonaId = null,
  categoryIds = null,
  channel = PRICE_RULE_CHANNELS.TPV,
}) {
  if (!product || !Array.isArray(rules) || rules.length === 0) return null;
  if (isLineExcluded(product, line)) return null;
  const moment = toMoment(at);
  if (!moment) return null;
  const base = basePrice != null ? Number(basePrice) : Number(product.precio);
  if (!Number.isFinite(base) || base <= 0) return null;
  const isWeight = isWeightProduct(product);

  let best = null;
  for (const rule of rules) {
    if (!rule || rule.isActive === false) continue;
    if (!channelMatches(rule, channel)) continue;
    if (!zoneMatches(rule, zonaId)) continue;
    if (!ruleTargetsProduct(rule, product, categoryIds)) continue;
    if (!ruleMatchesMoment(rule, moment)) continue;
    const price = computeRulePrice(rule, base, { isWeight });
    if (price == null) continue;
    const prio = Number(rule.priority) || 0;
    if (
      !best
      || prio > best.prio
      || (prio === best.prio && price < best.price)
      || (prio === best.prio && price === best.price && Number(rule.id) < Number(best.rule.id))
    ) {
      best = { rule, prio, price };
    }
  }
  if (!best) return null;
  return {
    precio: best.price,
    precioCarta: roundCents(base),
    priceRuleId: best.rule.id,
    priceRuleName: best.rule.name || "Promoción",
  };
}

// ── Textos para la UI ────────────────────────────────────────────────────

/** «L–V», «S–D», «L, X, V», «Todos los días». */
export function formatWeekdays(mask) {
  const m = Number(mask) > 0 ? Number(mask) : ALL_WEEKDAYS;
  if ((m & ALL_WEEKDAYS) === ALL_WEEKDAYS) return "Todos los días";
  const days = WEEKDAY_BITS.map((b, i) => ((m & b) !== 0 ? i : -1)).filter((i) => i >= 0);
  // Rango consecutivo de ≥3 días → «L–V».
  const consecutive = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  if (consecutive && days.length >= 3) {
    return `${WEEKDAY_SHORT[days[0]]}–${WEEKDAY_SHORT[days[days.length - 1]]}`;
  }
  return days.map((d) => WEEKDAY_SHORT[d]).join(", ");
}

/** «L–V 18:00–20:00» · «S–D todo el día» · «… · del 01/10 al 31/10». */
export function formatSchedule(rule) {
  const days = formatWeekdays(rule?.weekdays);
  const s = rule?.startTime ? String(rule.startTime).slice(0, 5) : null;
  const e = rule?.endTime ? String(rule.endTime).slice(0, 5) : null;
  const time = s || e ? `${s || "00:00"}–${e || "24:00"}` : "todo el día";
  let out = `${days} ${time}`;
  const fmt = (d) => {
    const [y, mo, da] = String(d).slice(0, 10).split("-");
    return `${da}/${mo}/${y}`;
  };
  if (rule?.validFrom && rule?.validTo) out += ` · del ${fmt(rule.validFrom)} al ${fmt(rule.validTo)}`;
  else if (rule?.validFrom) out += ` · desde ${fmt(rule.validFrom)}`;
  else if (rule?.validTo) out += ` · hasta ${fmt(rule.validTo)}`;
  return out;
}

export function formatKindValue(rule) {
  const v = Number(rule?.value);
  const n = Number.isFinite(v) ? v : 0;
  const money = n.toFixed(2).replace(".", ",");
  switch (String(rule?.kind || "").toUpperCase()) {
    case PRICE_RULE_KINDS.PERCENT_OFF:
      return `−${String(n).replace(".", ",")} %`;
    case PRICE_RULE_KINDS.AMOUNT_OFF:
      return `−${money} €`;
    case PRICE_RULE_KINDS.FIXED_PRICE:
      return `Precio fijo ${money} €`;
    default:
      return "—";
  }
}
